import { useEffect, useMemo, useRef, useState } from "react";
import { useNow } from "../hooks/useNow";
import { useFirestoreCollection } from "../hooks/useFirestoreCollection";
import {
  programmeringByMin,
  ecc as eccItems,
  aandachtTimed,
} from "../lib/data";
import {
  EVENT_DATE,
  formatHHMM,
  hhmmToMinutes,
  minutesNow,
  isCurrent,
} from "../lib/time";
import {
  addIncident,
  resolveIncident,
  setChecklistItem,
  getMelderString,
} from "../lib/firestore-ops";
import { QuickLog } from "./QuickLog";
import { CpLogButton } from "./CpLogButton";
import { Icon } from "./Icon";
import { SeverityIcon } from "./SeverityIcon";
import { useFreshHighlight } from "../hooks/useFreshHighlight";
import type {
  ChecklistEntry,
  IncidentEntry,
} from "../lib/types";

// Incidents created within this window are rendered with the "fresh" entry
// animation. 30 sec is generous enough that even a slow operator notices.
const FRESH_WINDOW_MS = 30_000;

type EntryKind = "act" | "ecc" | "aandacht" | "incident";

interface TimelineEntry {
  id: string;
  timeMin: number;
  timeLabel: string;
  kind: EntryKind;
  title: string;
  location?: string;
  body?: string;
  severity?: IncidentEntry["severity"];
  duration?: { vanMin: number; totMin: number | null };
  source?: string;
  resolved?: boolean;
  checklistId?: string;
  incidentId?: string;
  /** Real epoch ms — used to detect freshly-arrived entries for animation. */
  createdAt?: number;
}

const SEVERITY_BORDER: Record<NonNullable<IncidentEntry["severity"]>, string> =
  {
    info: "border-zinc-700",
    "let-op": "border-warn/50",
    actie: "border-accent/50",
    alarm: "border-risk/60",
  };

const SEVERITY_PILL: Record<NonNullable<IncidentEntry["severity"]>, string> = {
  info: "bg-zinc-700 text-zinc-200",
  "let-op": "bg-warn/20 text-warn",
  actie: "bg-accent/20 text-accent",
  alarm: "bg-risk/25 text-risk",
};

const SEVERITY_LABEL: Record<NonNullable<IncidentEntry["severity"]>, string> = {
  info: "Info",
  "let-op": "Let op",
  actie: "Actie",
  alarm: "Alarm",
};

type FilterId = "all" | "programmering" | "aandacht" | "ecc" | "incidents";

const FILTERS: { id: FilterId; label: string }[] = [
  { id: "all", label: "Alles" },
  { id: "programmering", label: "Acts" },
  { id: "aandacht", label: "Aandacht" },
  { id: "ecc", label: "ECC" },
  { id: "incidents", label: "Incidents" },
];

export function Timeline({
  onOpenBommelding,
}: {
  onOpenBommelding?: () => void;
} = {}) {
  const now = useNow(20_000);
  const nowMin = minutesNow(now);
  const [filter, setFilter] = useState<FilterId>("all");
  const [composing, setComposing] = useState(false);

  const { data: incidents } = useFirestoreCollection<IncidentEntry>(
    "incidents",
    { orderField: "time", orderDir: "asc" },
  );
  const { data: checklist } = useFirestoreCollection<ChecklistEntry>(
    "checklist",
  );
  const checkMap = useMemo(
    () => new Map(checklist.map((c) => [c.id, c])),
    [checklist],
  );

  const entries = useMemo<TimelineEntry[]>(() => {
    const out: TimelineEntry[] = [];

    for (const p of programmeringByMin) {
      if (p.is_overleg) continue;
      if (p.vanMin == null) continue;
      out.push({
        id: `act:${p.id}`,
        timeMin: p.vanMin,
        timeLabel: p.tijd_van!,
        kind: "act",
        title: p.wat,
        location: [p.locatie.venue, p.locatie.stage]
          .filter(Boolean)
          .join(" · "),
        body: p.aandachtspunten ?? undefined,
        duration: { vanMin: p.vanMin, totMin: p.totMin },
      });
    }

    for (const e of eccItems) {
      if (e.vanMin == null) continue;
      out.push({
        id: `ecc:${e.id}`,
        timeMin: e.vanMin,
        timeLabel: e.tijd_van!,
        kind: "ecc",
        title: e.wat,
        location: "ECC",
        body: e.aandachtspunten ?? undefined,
        checklistId: e.id,
      });
    }

    const seenTitles = new Set(out.map((o) => `${o.timeMin}|${o.title}`));
    for (const a of aandachtTimed) {
      const min = hhmmToMinutes(a.tijd);
      if (min == null) continue;
      const k = `${min}|${a.wat}`;
      if (seenTitles.has(k)) continue;
      out.push({
        id: `aandacht:${a.id}`,
        timeMin: min,
        timeLabel: a.tijd!,
        kind: "aandacht",
        title: a.wat ?? "Aandachtspunt",
        location: a.locatie ?? undefined,
        body: a.tekst,
        source: a.source.label,
      });
    }

    // SSOT: lib/time.ts EVENT_DATE — never inline an event date here (BP26 migration bug)
    const dayStart = new Date(EVENT_DATE + "T00:00:00+02:00").getTime();
    for (const i of incidents) {
      const d = new Date(i.time);
      const offset = (d.getTime() - dayStart) / 60000;
      out.push({
        id: `incident:${i.id}`,
        timeMin: Math.floor(offset),
        timeLabel: d.toLocaleTimeString("nl-NL", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        kind: "incident",
        title: i.title,
        location: i.location,
        body: i.body,
        severity: i.severity,
        resolved: i.resolved,
        incidentId: i.id,
        createdAt: i.time,
      });
    }

    return out.sort((a, b) => a.timeMin - b.timeMin);
  }, [incidents]);

  const filtered = entries.filter((e) => {
    if (filter === "all") return true;
    if (filter === "programmering") return e.kind === "act";
    if (filter === "aandacht")
      return e.kind === "aandacht" || (e.kind === "act" && e.body);
    if (filter === "ecc") return e.kind === "ecc";
    if (filter === "incidents") return e.kind === "incident";
    return true;
  });

  const nowIdx = filtered.findIndex((e) => e.timeMin > nowMin);

  const nowRef = useRef<HTMLDivElement | null>(null);
  const scrolledOnce = useRef(false);
  useEffect(() => {
    if (scrolledOnce.current) return;
    if (filtered.length === 0) return;
    scrolledOnce.current = true;
    requestAnimationFrame(() => {
      nowRef.current?.scrollIntoView({ block: "center", behavior: "instant" });
    });
  }, [filtered.length]);

  // "Nu"-volgsysteem: knop zodra de nu-lijn uit beeld is; zacht meevolgen
  // wanneer de operator 90s niet zelf scrolt; recentreren bij app-terugkeer.
  const [nowVisible, setNowVisible] = useState(true);
  const lastUserScroll = useRef(0);
  const scrollToNow = (smooth = true) =>
    nowRef.current?.scrollIntoView({
      block: "center",
      behavior: smooth ? "smooth" : "instant",
    });

  useEffect(() => {
    const el = nowRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setNowVisible(entry.isIntersecting),
      { rootMargin: "-10% 0px -10% 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [nowIdx, filtered.length]);

  useEffect(() => {
    const onScroll = () => {
      lastUserScroll.current = Date.now();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") scrollToNow(false);
    };
    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    // Tick-gebonden meevolgen: alleen als de gebruiker al 90s stilzit
    if (nowVisible) return;
    if (Date.now() - lastUserScroll.current < 90_000) return;
    scrollToNow();
  }, [nowMin, nowVisible]);

  const counts = useMemo(() => {
    const c: Record<FilterId, number> = {
      all: entries.length,
      programmering: 0,
      aandacht: 0,
      ecc: 0,
      incidents: 0,
    };
    for (const e of entries) {
      if (e.kind === "act") c.programmering++;
      else if (e.kind === "ecc") c.ecc++;
      else if (e.kind === "incident") c.incidents++;
      else if (e.kind === "aandacht") c.aandacht++;
    }
    c.aandacht += entries.filter((e) => e.kind === "act" && e.body).length;
    return c;
  }, [entries]);

  return (
    <div>
      {/* Sticky: de log-knop blijft in beeld tijdens scrollen (showday-eis 11-07) */}
      <div className="sticky top-0 z-20 -mx-1 px-1 pt-1 pb-2 bg-ink-950/95 backdrop-blur-sm relative">
        {composing ? (
          <QuickLog
            onOpenBommelding={onOpenBommelding}
            onSubmit={async (i) => {
              await addIncident(i);
              setComposing(false);
            }}
            onCancel={() => setComposing(false)}
          />
        ) : (
          <button
            onClick={() => setComposing(true)}
            className="w-full card p-3 flex items-center gap-2 text-zinc-300 active:bg-ink-700 hover:bg-ink-700/40 transition-colors min-h-[44px]"
          >
            <Icon.Plus className="text-accent w-5 h-5" />
            <span>Log een gebeurtenis</span>
          </button>
        )}
        {!nowVisible && (
          <button
            onClick={() => scrollToNow()}
            className="absolute right-2 -bottom-9 z-20 pill bg-accent/20 text-accent border border-accent/40 px-3 py-1.5 text-xs font-medium shadow-lg"
          >
            → Nu {formatHHMM(now)}
          </button>
        )}
      </div>

      <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-2 mb-2 text-xs no-scrollbar">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            aria-pressed={filter === f.id}
            className={`shrink-0 px-3 py-2 min-h-[40px] rounded-full border whitespace-nowrap transition-colors ${
              filter === f.id
                ? "bg-accent/15 border-accent/40 text-accent"
                : "border-line text-zinc-400 hover:text-zinc-200 active:bg-ink-700"
            }`}
          >
            {f.label}{" "}
            <span className="opacity-60 tabular ml-0.5">
              {counts[f.id] ?? entries.length}
            </span>
          </button>
        ))}
      </div>

      <ol className="relative">
        <div className="absolute left-[3.25rem] top-0 bottom-0 w-px bg-line" />
        {filtered.length === 0 && (
          <li className="text-2xs text-zinc-500 px-1 py-6 text-center">
            Geen items voor dit filter.
          </li>
        )}
        {filtered.map((e, idx) => {
          const insertNowHere = idx === nowIdx;
          const past = e.timeMin <= nowMin;
          const happeningNow =
            e.duration &&
            isCurrent(e.duration.vanMin, e.duration.totMin, nowMin);
          return (
            <div key={e.id}>
              {insertNowHere && <NowMarker ref={nowRef} now={now} />}
              <Row
                entry={e}
                past={past}
                happeningNow={happeningNow ?? false}
                checked={
                  e.checklistId
                    ? (checkMap.get(e.checklistId)?.checked ?? false)
                    : false
                }
                onToggleCheck={
                  e.checklistId
                    ? () =>
                        setChecklistItem(
                          e.checklistId!,
                          !checkMap.get(e.checklistId!)?.checked,
                          getMelderString(),
                        )
                    : undefined
                }
                onResolveIncident={
                  e.incidentId
                    ? () => resolveIncident(e.incidentId!, !e.resolved)
                    : undefined
                }
              />
            </div>
          );
        })}
        {nowIdx === -1 && filtered.length > 0 && (
          <NowMarker ref={nowRef} now={now} trailing />
        )}
      </ol>
    </div>
  );
}

const NowMarker = ({
  now,
  trailing,
  ref,
}: {
  now: Date;
  trailing?: boolean;
  ref: React.Ref<HTMLDivElement>;
}) => (
  <div
    ref={ref}
    className={`relative my-3 ${trailing ? "pt-2" : ""}`}
    aria-label="Nu"
  >
    <div className="flex items-center gap-2">
      <div className="w-12 shrink-0 text-2xs uppercase tracking-[0.18em] font-semibold text-accent text-right pr-2">
        Nu
      </div>
      <div className="w-2 h-2 rounded-full bg-accent shadow-[0_0_8px_2px_rgba(255,107,31,0.6)]" />
      <div className="flex-1 h-px bg-gradient-to-r from-accent/60 to-transparent" />
      <div className="text-2xs tabular text-accent">{formatHHMM(now)}</div>
    </div>
  </div>
);

function Row({
  entry,
  past,
  happeningNow,
  checked,
  onToggleCheck,
  onResolveIncident,
}: {
  entry: TimelineEntry;
  past: boolean;
  happeningNow: boolean;
  checked: boolean;
  onToggleCheck?: () => void;
  onResolveIncident?: () => void;
}) {
  const isFresh =
    entry.kind === "incident" &&
    entry.createdAt != null &&
    Date.now() - entry.createdAt < FRESH_WINDOW_MS;
  const cardRef = useFreshHighlight<HTMLDivElement>(isFresh);
  const sevBorder = entry.severity ? SEVERITY_BORDER[entry.severity] : "";
  const sevPill = entry.severity ? SEVERITY_PILL[entry.severity] : "";
  const sevLabel = entry.severity ? SEVERITY_LABEL[entry.severity] : "";

  const dotColor =
    entry.kind === "incident" && entry.severity
      ? entry.severity === "alarm"
        ? "bg-risk"
        : entry.severity === "actie"
          ? "bg-accent"
          : entry.severity === "let-op"
            ? "bg-warn"
            : "bg-zinc-500"
      : entry.kind === "ecc"
        ? "bg-warn"
        : entry.kind === "aandacht"
          ? "bg-warn/80"
          : happeningNow
            ? "bg-ok"
            : "bg-zinc-600";

  const dimmed = past && !happeningNow && !entry.severity;

  return (
    <li className={`relative flex items-start gap-3 py-2 ${dimmed ? "opacity-60" : ""}`}>
      <div className="w-12 shrink-0 text-right pt-1.5 pr-2">
        <div className="text-sm font-mono tabular text-zinc-300 leading-none">
          {entry.timeLabel}
        </div>
      </div>
      <div className="relative w-2 shrink-0 flex justify-center pt-2.5">
        <div
          className={`w-2 h-2 rounded-full ${dotColor} ${happeningNow ? "animate-pulseSoft" : ""} ring-2 ring-ink-950`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div
          ref={cardRef}
          className={`card px-3 py-2 ${sevBorder ? `border ${sevBorder}` : ""} ${happeningNow ? "border-ok/40" : ""} ${entry.checklistId && checked ? "opacity-60" : ""}`}
        >
          <div className="flex items-start gap-2">
            {entry.checklistId && (
              <button
                onClick={onToggleCheck}
                aria-label={`${checked ? "Vink uit" : "Vink af"}: ${entry.title}`}
                aria-pressed={checked}
                className="w-9 h-9 -ml-1.5 -mt-1 flex items-center justify-center shrink-0 active:bg-ink-700 rounded-lg"
              >
                <span
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    checked
                      ? "bg-ok border-ok text-ink-950"
                      : "border-line text-zinc-500"
                  }`}
                >
                  {checked && <Icon.Check className="w-3 h-3" />}
                </span>
              </button>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <KindBadge kind={entry.kind} />
                {sevLabel && entry.severity && (
                  <span className={`pill ${sevPill}`}>
                    <SeverityIcon
                      severity={entry.severity}
                      className="w-2.5 h-2.5"
                    />
                    {sevLabel}
                  </span>
                )}
                {happeningNow && (
                  <span className="pill bg-ok/15 text-ok">
                    <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulseSoft" />
                    Live
                  </span>
                )}
                {entry.resolved && (
                  <span className="pill bg-ok/10 text-zinc-400">
                    afgehandeld
                  </span>
                )}
              </div>
              <div
                className={`text-sm mt-1 ${
                  checked || entry.resolved
                    ? "line-through text-zinc-500"
                    : "text-zinc-100 font-medium"
                }`}
              >
                {entry.title}
              </div>
              {entry.location && (
                <div className="text-2xs text-zinc-500 mt-0.5">
                  {entry.location}
                </div>
              )}
              {entry.duration && (
                <div className="text-2xs text-zinc-500 mt-0.5 tabular">
                  loopt tot{" "}
                  {entry.duration.totMin != null
                    ? mmToHHMM(entry.duration.totMin)
                    : "?"}
                </div>
              )}
              {entry.body && (
                <div className="text-2xs text-zinc-400 mt-1.5 whitespace-pre-wrap leading-relaxed">
                  {entry.body}
                </div>
              )}
              {entry.source && (
                <div className="text-2xs text-zinc-600 mt-1.5">
                  bron: {entry.source}
                </div>
              )}
              {onResolveIncident && (
                <button
                  onClick={onResolveIncident}
                  className="text-xs text-zinc-400 mt-2 px-2 py-1.5 -ml-2 rounded active:bg-ink-700 hover:text-zinc-100 min-h-[36px]"
                >
                  {entry.resolved ? "Heropen" : "Sluit af"}
                </button>
              )}
              <CpLogButton
                entryId={entry.id}
                defaultTijd={entry.timeLabel}
                defaultBericht={
                  entry.title +
                  (entry.location ? ` — ${entry.location}` : "")
                }
              />
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

function mmToHHMM(min: number): string {
  const h = Math.floor((min % (24 * 60)) / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function KindBadge({ kind }: { kind: EntryKind }) {
  const map: Record<EntryKind, { label: string; cls: string }> = {
    act: { label: "Act", cls: "bg-zinc-800 text-zinc-300" },
    ecc: { label: "ECC", cls: "bg-warn/15 text-warn" },
    aandacht: { label: "Aandacht", cls: "bg-warn/15 text-warn" },
    incident: { label: "Log", cls: "bg-accent/15 text-accent" },
  };
  const m = map[kind];
  return <span className={`pill ${m.cls}`}>{m.label}</span>;
}
